import { Formik } from "formik";
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  CloudOff,
  Loader2,
  Search,
  UserRound,
} from "lucide-react";
import { useCallback, useId, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useSyncPatientByEhr } from "@elation/api-client/src/stitch-web";
import type {
  EhrConnector,
  PatientSearchConnectorError,
  PatientSearchItem,
} from "@elation/api-client/src/stitch-web/models";
import type { SearchPatientsSortBy } from "@elation/api-client/src/stitch-web/models/searchPatientsSortBy";

import { useIsPhysician } from "@src/features/physicians";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@src/shared/components/ui/alert";
import { Avatar, AvatarFallback } from "@src/shared/components/ui/avatar";
import { Badge } from "@src/shared/components/ui/badge";
import { Button } from "@src/shared/components/ui/button";
import { Input } from "@src/shared/components/ui/input";
import { Label } from "@src/shared/components/ui/label";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import {
  isStaleCursorError,
  useCursorPagination,
  useResetStaleCursor,
} from "@src/shared/hooks/useCursorPagination";
import { cn } from "@src/shared/lib/utils";
import { navigationV2 } from "@src/shared/navigation/v2";
import { getAssetUrl } from "@src/shared/utils/assets/getAssetUrl";
import { getDisplayErrorMessage } from "@src/shared/utils/errors";
import { captureException } from "@src/shared/utils/observability/sentry";

import { usePatientsSearchQuery } from "../../../../data";
import { PatientEntity } from "../../../../domain";
import { CreatePatientDialogV2 } from "../CreatePatientDialog";

type PatientFilters = {
  q?: string;
};

type PatientSearchFormValues = {
  q: string;
};

type SortState = {
  sortBy: SearchPatientsSortBy;
  order: "asc" | "desc";
};

const DEFAULT_SORT: SortState = { sortBy: "lastName", order: "asc" };

const EHR_LOGOS: Record<EhrConnector, { src: string; className: string }> = {
  elation: { src: "/images/elation-logo.svg", className: "h-3.5" },
  athenahealth: { src: "/images/athena-logo.svg", className: "h-3" },
  eclinicalworks: {
    src: "/images/eclinical-works-logo.svg",
    className: "h-2.5",
  },
  redoxfhirsandbox: { src: "/images/redox-logo-sandbox.svg", className: "h-3" },
};

function ConnectorErrorAlert({
  ehr,
  error,
}: {
  ehr: EhrConnector;
  error: PatientSearchConnectorError;
}) {
  const ehrLabel = PatientEntity.ehrConnectorLabel(ehr);

  return (
    <Alert
      variant="destructive"
      className="rounded-card border-destructive/40 bg-destructive/5"
      aria-live="polite"
    >
      <AlertCircle className="size-4" aria-hidden="true" />
      <AlertTitle>{ehrLabel} search failed</AlertTitle>
      <AlertDescription>{error.message}</AlertDescription>
    </Alert>
  );
}

function PatientsListSkeleton() {
  return (
    <VerticalFlexbox
      gap="md"
      className="rounded-card border border-border bg-surface p-card shadow-soft"
      aria-label="Loading patients"
      aria-busy="true"
    >
      {[0, 1, 2, 3].map((i) => (
        <HorizontalFlexbox key={i} alignItems="center" className="gap-3">
          <Skeleton className="size-10 rounded-full" />
          <VerticalFlexbox gap="sm" className="min-w-0 flex-1">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-64 max-w-full" />
          </VerticalFlexbox>
          <Skeleton className="h-6 w-20 rounded-full" />
        </HorizontalFlexbox>
      ))}
    </VerticalFlexbox>
  );
}

function EmptyPatientsList() {
  return (
    <VerticalFlexbox
      alignItems="center"
      justifyContent="center"
      className="min-h-[420px] rounded-card border border-dashed border-border bg-surface px-8 py-12 text-center"
    >
      <UserRound className="mb-4 size-9 text-primary" aria-hidden="true" />
      <h2 className="text-xl font-semibold text-surface-foreground">
        No patients found
      </h2>
      <p className="mt-2 max-w-[360px] text-sm leading-6 text-muted-foreground">
        Try a different search or create a new patient record.
      </p>
    </VerticalFlexbox>
  );
}

function ErrorPatientsList({ message }: { message?: string }) {
  return (
    <Alert
      variant="destructive"
      className="rounded-card border-destructive/40 bg-destructive/5 p-card"
    >
      <AlertCircle className="size-4" aria-hidden="true" />
      <AlertTitle>Unable to load patients</AlertTitle>
      <AlertDescription>
        {message ?? "Something went wrong retrieving patients."}
      </AlertDescription>
    </Alert>
  );
}

function PatientSyncBadge({ patient }: { patient: PatientSearchItem }) {
  const isSynced = PatientEntity.isSynced(patient);

  if (isSynced) {
    return (
      <Badge
        variant="success"
        className="gap-1 rounded-full border-transparent bg-success/10 px-1.5 py-0 text-[10px] text-success hover:bg-success/10"
      >
        <CheckCircle2 className="size-2.5" aria-hidden="true" />
        Synced
      </Badge>
    );
  }

  if (patient.syncStatus === "not_synced") {
    return (
      <Badge
        variant="warning"
        className="gap-1 rounded-full border-transparent bg-warning/10 px-1.5 py-0 text-[10px] text-warning hover:bg-warning/10"
      >
        <CloudOff className="size-2.5" aria-hidden="true" />
        Not synced
      </Badge>
    );
  }

  return (
    <Badge variant="subtle" className="rounded-full px-1.5 py-0 text-[10px]">
      Sync unknown
    </Badge>
  );
}

function PatientEhrLogo({ patient }: { patient: PatientSearchItem }) {
  if (!patient.ehr) {
    return (
      <Badge variant="subtle" className="rounded-full px-1.5 py-0 text-[10px]">
        Unknown EHR
      </Badge>
    );
  }

  const logo = EHR_LOGOS[patient.ehr];
  const ehrLabel = PatientEntity.ehrConnectorLabel(patient.ehr);

  return (
    <span className="inline-flex h-6 items-center rounded-full px-1.5">
      <img
        src={getAssetUrl(logo.src)}
        alt={ehrLabel}
        className={cn("w-auto max-w-[120px] object-contain", logo.className)}
      />
    </span>
  );
}

function PatientRow({
  patient,
  isOpening,
  onPatientSelect,
}: {
  patient: PatientSearchItem;
  isOpening: boolean;
  onPatientSelect?: (patient: PatientSearchItem) => void;
}) {
  const name = PatientEntity.displayName(patient) ?? "Unknown patient";
  const metadata = PatientEntity.listMetadata(patient);

  return (
    <li aria-label={PatientEntity.accessibleListItemName(patient) ?? name}>
      <Button
        type="button"
        variant="ghost"
        className={cn(
          "h-auto w-full justify-start gap-3 whitespace-normal rounded-none border-b border-border bg-transparent px-[18px] py-[13px] text-left font-normal shadow-none hover:bg-muted/40 hover:text-foreground focus-visible:bg-muted/40 focus-visible:ring-0 focus-visible:ring-offset-0",
          isOpening && "pointer-events-none opacity-70",
        )}
        disabled={!onPatientSelect || isOpening}
        onClick={() => onPatientSelect?.(patient)}
      >
        <Avatar className="size-10 border border-border bg-background">
          <AvatarFallback className="bg-primary/10 font-mono text-[11px] font-semibold uppercase text-primary">
            {PatientEntity.initials(patient)}
          </AvatarFallback>
        </Avatar>

        <VerticalFlexbox className="min-w-0 flex-1">
          <HorizontalFlexbox
            alignItems="center"
            flexWrap="wrap"
            className="gap-x-2 gap-y-1"
          >
            <span className="truncate text-sm font-semibold text-surface-foreground">
              {name}
            </span>
            <PatientEhrLogo patient={patient} />
            <PatientSyncBadge patient={patient} />
          </HorizontalFlexbox>
          {metadata ? (
            <p className="mt-1 font-mono text-[10.5px] text-muted-foreground">
              {metadata}
            </p>
          ) : null}
        </VerticalFlexbox>

        {isOpening ? (
          <Loader2
            className="size-4 shrink-0 animate-spin text-muted-foreground"
            aria-hidden="true"
          />
        ) : (
          <ChevronRight
            className="size-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        )}
      </Button>
    </li>
  );
}

interface PatientsListContentProps {
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  patients: PatientSearchItem[];
  openingPatientKey: string | null;
  openPatient: (patient: PatientSearchItem) => void | Promise<void>;
}

function PatientsListContent({
  isLoading,
  isError,
  errorMessage,
  patients,
  openingPatientKey,
  openPatient,
}: PatientsListContentProps) {
  if (isLoading) return <PatientsListSkeleton />;
  if (isError) return <ErrorPatientsList message={errorMessage} />;
  if (patients.length === 0) return <EmptyPatientsList />;

  return (
    <ul
      aria-label="Patients"
      className="overflow-hidden rounded-card border border-border bg-surface shadow-soft"
    >
      {patients.map((patient) => {
        const key = PatientEntity.searchResultKey(patient);
        return (
          <PatientRow
            key={key}
            patient={patient}
            isOpening={openingPatientKey === key}
            onPatientSelect={openPatient}
          />
        );
      })}
    </ul>
  );
}

export function PatientsListV2() {
  const canCreatePatient = useIsPhysician();
  const navigate = useNavigate();
  const searchInputId = useId();
  const [filters, setFilters] = useState<PatientFilters>({});
  const [sort] = useState<SortState>(DEFAULT_SORT);
  const [openingPatientKeyInternal, setOpeningPatientKeyInternal] = useState<
    string | null
  >(null);
  const {
    currentCursor,
    goToNextPage,
    goToPreviousPage,
    hasPrevPage,
    resetCursor,
  } = useCursorPagination();
  const patientsQuery = usePatientsSearchQuery({
    ...filters,
    ...sort,
    cursor: currentCursor,
  });
  const syncPatientByEhr = useSyncPatientByEhr();
  const patientsInternal = useMemo(
    () => patientsQuery.data?.data.items ?? [],
    [patientsQuery.data],
  );
  const nextCursor = patientsQuery.data?.data.nextCursor ?? null;
  const isStaleCursor = isStaleCursorError({
    currentCursor,
    error: patientsQuery.error,
    isError: patientsQuery.isError,
  });
  const patients = patientsInternal;
  const searchErrors = patientsQuery.data?.data.errors ?? {};
  const isLoading = patientsQuery.isLoading;
  const isError = patientsQuery.isError && !isStaleCursor;
  const errorMessage =
    getDisplayErrorMessage(patientsQuery.error, {
      fallback: "Unable to load patients.",
    }) ?? undefined;
  const openingPatientKey = openingPatientKeyInternal;
  const hasNextPage = Boolean(nextCursor);
  const searchErrorEntries = Object.entries(searchErrors) as Array<
    [EhrConnector, PatientSearchConnectorError]
  >;

  useResetStaleCursor(isStaleCursor, resetCursor);

  const searchPatients = useCallback(
    (q: string) => {
      setFilters({ q: q.trim() || undefined });
      resetCursor();
    },
    [resetCursor],
  );

  const openPatient = useCallback(
    async (patient: PatientSearchItem) => {
      const key = PatientEntity.searchResultKey(patient);
      setOpeningPatientKeyInternal(key);

      try {
        if (patient.patientId != null) {
          navigate(navigationV2.patientDetail(String(patient.patientId)), {
            state: { source: "v2 patient list" },
          });
          return;
        }

        if (!patient.ehr || patient.externalPatientId == null) {
          toast.error("Unable to open this patient.");
          return;
        }

        const result = await syncPatientByEhr.mutateAsync({
          ehr: patient.ehr,
          patientId: String(patient.externalPatientId),
        });
        const patientId = result.data.patient.id;

        navigate(navigationV2.patientDetail(String(patientId)), {
          state: { source: "v2 patient list" },
        });
      } catch (error) {
        captureException(error, {
          tags: { feature: "patients-v2", action: "open-patient" },
          extra: {
            ehr: patient.ehr,
            externalPatientId: patient.externalPatientId,
            patientId: patient.patientId,
          },
        });
        toast.error("Unable to open this patient. Please try again.");
      } finally {
        setOpeningPatientKeyInternal(null);
      }
    },
    [navigate, syncPatientByEhr],
  );

  const ehrSearchErrors =
    searchErrorEntries.length > 0 ? (
      <VerticalFlexbox gap="sm" className="mb-3">
        {searchErrorEntries.map(([ehr, error]) => (
          <ConnectorErrorAlert key={ehr} ehr={ehr} error={error} />
        ))}
      </VerticalFlexbox>
    ) : null;

  return (
    <>
      <Formik<PatientSearchFormValues>
        initialValues={{ q: filters.q ?? "" }}
        enableReinitialize
        onSubmit={(values) => searchPatients(values.q)}
      >
        {({ handleChange, handleSubmit, values }) => (
          <form onSubmit={handleSubmit} className="grid gap-2" role="search">
            <Label htmlFor={searchInputId} className="sr-only">
              Search patients
            </Label>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                id={searchInputId}
                name="q"
                value={values.q}
                type="search"
                placeholder="Search patients"
                autoComplete="off"
                className="h-9 rounded-[9px] bg-surface pl-9"
                onChange={handleChange}
              />
            </div>

            <CreatePatientDialogV2 canCreatePatient={canCreatePatient} />
          </form>
        )}
      </Formik>

      {ehrSearchErrors}
      <div className="mt-4">
        <PatientsListContent
          isLoading={isLoading}
          isError={isError}
          errorMessage={errorMessage}
          patients={patients}
          openingPatientKey={openingPatientKey}
          openPatient={openPatient}
        />
      </div>

      {hasPrevPage || hasNextPage ? (
        <HorizontalFlexbox
          alignItems="center"
          justifyContent="flex-end"
          gap="xs"
          className="mt-4"
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasPrevPage}
            onClick={goToPreviousPage}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasNextPage}
            onClick={() => goToNextPage(nextCursor)}
          >
            Next
          </Button>
        </HorizontalFlexbox>
      ) : null}
    </>
  );
}
